<template>
  <section class="bills-list section-card">
    <header class="section-card__header">
      <div>
        <h2 class="section-card__title">
          <i class="fa-regular fa-file-invoice section-card__icon" />
          {{ t('billing.subscriptionPage.bills.title') }}
        </h2>
        <p class="section-card__subtitle">
          {{ t('billing.subscriptionPage.bills.subtitle') }}
        </p>
      </div>
    </header>

    <!-- Shopify shops: invoices live in the Shopify back-office. -->
    <div
      v-if="isShopify"
      class="bills-list__shopify"
    >
      <div class="bills-list__shopify-icon">
        <i class="fa-brands fa-shopify" />
      </div>
      <div>
        <div class="bills-list__shopify-title">
          {{ t('billing.subscriptionPage.bills.shopifyTitle') }}
        </div>
        <div class="bills-list__shopify-text">
          {{ t('billing.subscriptionPage.bills.shopifyText') }}
        </div>
      </div>
    </div>

    <SpmTableAPI
      v-else
      ref="tableRef"
      :api-call="apiCall"
      :columns="columns"
      data-key="shops_bills.id_ps_bundle"
      :initial-rows="10"
      :rows-per-page-options="[10, 25, 50, 100]"
      :initial-sort="initialSort"
      :show-reload-button="true"
    >
      <!-- DataService aliases every column as `${mainTable}.${field}` in the
           SELECT (cf. `buildSelectClauseWithJoins`). The row keys are thus
           `shops_bills.<col>` — slots read via bracket access. -->
      <template #cell-date="{ data }">
        {{ formatBillDate(data['shops_bills.date']) }}
      </template>

      <template #cell-description="{ data }">
        {{ formatProducts(data['shops_bills.products']) }}
      </template>

      <template #cell-amountHt="{ data }">
        {{ formatBillAmount(data['shops_bills.amount_ht']) }}
      </template>

      <template #cell-amountTtc="{ data }">
        <strong>{{ formatBillAmount(data['shops_bills.amount_ttc']) }}</strong>
      </template>

      <template #cell-status="{ data }">
        <span
          class="bills-list__status"
          :class="statusClass(data['shops_bills.payment_error'])"
        >
          <i :class="statusIcon(data['shops_bills.payment_error'])" />
          {{ statusLabel(data['shops_bills.payment_error']) }}
        </span>
      </template>

      <template #cell-actions="{ data }">
        <div class="bills-list__row-actions">
          <Button
            v-if="data['shops_bills.payment_error'] === 2"
            :label="t('billing.subscriptionPage.bills.actions.pay')"
            class="p-button-sm bills-list__pay-btn"
            @click="$emit('pay', data)"
          />
          <Button
            v-if="data['shops_bills.payment_error'] === 0 || data['shops_bills.payment_error'] === 2"
            icon="fa-regular fa-file-pdf"
            :label="data['shops_bills.id_ps_bundle']"
            class="p-button-text p-button-sm bills-list__download-btn"
            :title="t('billing.subscriptionPage.bills.actions.download')"
            @click="$emit('download', data)"
          />
        </div>
      </template>
    </SpmTableAPI>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import Button from 'primevue/button';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import type {
  ApiCallConfig,
  SpmActiveSort,
  SpmColumnConfig,
} from '@/components/table-rest-api/types';
import { NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

export default defineComponent({
  name: 'BillsList',

  components: { Button, SpmTableAPI },

  props: {
    /** Shopify shops manage invoices externally — render a notice instead of
     *  the table. */
    isShopify: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['pay', 'download'],

  setup(props, { expose }) {
    const { t } = useI18n();
    const tableRef = ref<any>(null);

    // `/billing/*` requires `?idShop=` in the URL (PermissionGuard +
    // ParseIntPipe). SpmTableAPI appends its StandardQueryListing params via
    // axios — putting `idShop` in the path keeps both in the final URL
    // (axios joins with `&` when the path already contains `?`).
    const apiCall = computed<ApiCallConfig>(() => ({
      type_api: NestApiType.PAYMENTS,
      path: `/billing/invoices?idShop=${UserState.activeShop?.id ?? 0}`,
      secure: true,
    }));

    // Sort by `payment_error` desc so unpaid invoices (`payment_error = 2`)
    // surface above processing (1) and paid (0) ones on the first render.
    // The user can re-sort by date or any other column afterwards — this is
    // only the initial state.
    const initialSort: SpmActiveSort = {
      field: 'payment_error',
      order: 'desc',
    };

    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'date',
        header: t('billing.subscriptionPage.bills.col.date'),
        fields: [
          {
            name: 'date',
            label: t('billing.subscriptionPage.bills.col.date'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
        ],
      },
      {
        id: 'description',
        header: t('billing.subscriptionPage.bills.col.description'),
        fields: ['products'],
      },
      {
        id: 'amountHt',
        header: t('billing.subscriptionPage.bills.col.amountHt'),
        fields: [
          {
            name: 'amount_ht',
            label: t('billing.subscriptionPage.bills.col.amountHt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'numberInput' },
          },
        ],
      },
      {
        id: 'amountTtc',
        header: t('billing.subscriptionPage.bills.col.amountTtc'),
        fields: [
          {
            name: 'amount_ttc',
            label: t('billing.subscriptionPage.bills.col.amountTtc'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'numberInput' },
          },
        ],
      },
      {
        id: 'status',
        header: t('billing.subscriptionPage.bills.col.status'),
        fields: [
          {
            name: 'payment_error',
            label: t('billing.subscriptionPage.bills.col.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: [
                { label: t('billing.subscriptionPage.bills.status.paid'), value: 0 },
                { label: t('billing.subscriptionPage.bills.status.processing'), value: 1 },
                { label: t('billing.subscriptionPage.bills.status.unpaid'), value: 2 },
              ],
            },
          },
        ],
      },
      {
        id: 'actions',
        header: t('billing.subscriptionPage.bills.col.actions'),
        // `id_ps_bundle` is rendered as the download button label AND used
        // by the row-level pay/download emits — fetched as a raw field so
        // it's present in `data` for the slot.
        fields: ['id_ps_bundle'],
      },
    ]);

    // ─── Cell formatters ───────────────────────────────────────────────────

    // `shops_bills.date` is a naive Paris-wall-clock DATETIME. Prisma reads
    // it as a Date whose UTC components equal that wall-clock — `moment.utc`
    // reads from those components so the display preserves the stored value
    // (no browser-TZ shift).
    const formatBillDate = (val: any): string => {
      if (!val) return '';
      return moment.utc(val).format('YYYY-MM-DD HH:mm:ss');
    };

    // `shops_bills.products` ships in three shapes across creation paths :
    //   - Zend legacy      : JSON array of strings (`["Crédits SMS FR (555)"]`)
    //   - New billing flow : JSON object of items with `.name`
    //   - Very old rows    : a plain pre-formatted string
    const formatProducts = (productsJson: any): string => {
      if (productsJson == null) return '—';
      const raw = String(productsJson).trim();
      if (!raw) return '—';
      try {
        const parsed = JSON.parse(raw);
        const extract = (p: any): string => {
          if (typeof p === 'string') return p;
          if (p && typeof p === 'object') return p.name ?? p.label ?? p.title ?? '';
          return '';
        };
        let labels: string[] = [];
        if (Array.isArray(parsed)) {
          labels = parsed.map(extract).filter(Boolean);
        } else if (parsed && typeof parsed === 'object') {
          labels = Object.values(parsed).map(extract).filter(Boolean);
        }
        if (labels.length > 0) return labels.join(', ');
      } catch {
        /* not JSON — fall through */
      }
      return raw;
    };

    // Currency follows the shop rule (Shopify = USD, others = EUR). The
    // Shopify table is hidden behind `v-else` so this code path is currently
    // EUR-only in practice, but keeping the rule uniform avoids a silent
    // regression the day we surface invoices for Shopify too.
    const formatBillAmount = (amount: any): string => {
      if (amount === null || amount === undefined) return '—';
      const n = Number(amount);
      if (!Number.isFinite(n)) return '—';
      const currency: 'EUR' | 'USD' = props.isShopify ? 'USD' : 'EUR';
      return n.toLocaleString(currency === 'USD' ? 'en-US' : 'fr-FR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const statusLabel = (pe: number) => {
      if (pe === 0) return t('billing.subscriptionPage.bills.status.paid');
      if (pe === 2) return t('billing.subscriptionPage.bills.status.unpaid');
      return t('billing.subscriptionPage.bills.status.processing');
    };

    const statusClass = (pe: number) => {
      if (pe === 0) return 'bills-list__status--paid';
      if (pe === 2) return 'bills-list__status--unpaid';
      return 'bills-list__status--processing';
    };

    const statusIcon = (pe: number) => {
      if (pe === 0) return 'fa-regular fa-circle-check';
      if (pe === 2) return 'fa-regular fa-circle-exclamation';
      return 'fa-regular fa-clock';
    };

    /** Exposed so the parent can reload the table after a successful repay. */
    const reloadData = async () => {
      if (tableRef.value?.reloadData) {
        await tableRef.value.reloadData();
      }
    };

    expose({ reloadData });

    return {
      t,
      tableRef,
      apiCall,
      columns,
      initialSort,
      formatBillDate,
      formatProducts,
      formatBillAmount,
      statusLabel,
      statusClass,
      statusIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.bills-list {
  &__shopify {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: linear-gradient(135deg, #f7fbf2 0%, #ffffff 100%);
    border-radius: 12px;
    border: 1px solid #ecf3e3;
  }

  &__shopify-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #95bf47;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  &__shopify-title {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 2px;
  }

  &__shopify-text {
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8125rem;
    font-weight: 500;

    &--paid { color: #629d03; }
    &--processing { color: #1d4ed8; }
    &--unpaid { color: #b91c1c; }
  }

  &__row-actions {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }

  &__pay-btn {
    background: #ef4444 !important;
    border-color: #ef4444 !important;
    color: #fff !important;

    &:hover {
      background: #b91c1c !important;
      border-color: #b91c1c !important;
    }
  }

  // Download button uses the invoice ref as its label — monospace for
  // legibility, matching the legacy ShopBills behaviour.
  &__download-btn :deep(.p-button-label) {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8125rem;
    letter-spacing: 0.02em;
  }
}
</style>
